import type {
  SocialTrackerItem,
  SocialTrackerProposal,
  SocialTrackerInput,
  SocialTrackerUpdate,
  SocialTrackerLabel,
  SocialTrackerLabelKind,
} from '@digital-twins/shared-types'
import type {
  TrackerFormState,
  RequiredField,
} from '@/types/components/social-tracker-dialog'

export const MAX_TRACKER_TERMS = 20
export const MAX_TRACKER_TERM_LENGTH = 100
export const MAX_TRACKER_LABELS = 10

/** Literal comma-separated input only; this does not interpret topic meaning. */
export function addSocialTerms(current: string[], draft: string): string[] {
  return [
    ...new Set([
      ...current,
      ...draft
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    ]),
  ]
}

export function spacedHashtagKeywords(form: TrackerFormState): Array<string> {
  if (form.platform !== 'instagram') {
    return []
  }
  return form.keywords.filter((keyword) => /\s/.test(keyword.trim()))
}

export function initialFormState(
  tracker: SocialTrackerItem | null,
  proposal: SocialTrackerProposal | null,
): TrackerFormState {
  const source = tracker ?? proposal
  return {
    name: source?.name ?? '',
    platform: source?.platform ?? 'reddit',
    keywords: source?.keywords ?? [],
    handles: source?.handles ?? [],
    language: source?.language ?? 'en',
    market: source?.market ?? '',
    labels: source?.labels ?? [],
    brief: source?.brief ?? { include: '', exclude: '' },
    keywordNotes: source?.keywordNotes ?? [],
    topics: source?.topics ?? [],
  }
}

/** The brief as the API takes it: notes of current keywords, named topics only. */
export function relevanceFields(form: TrackerFormState) {
  return {
    brief: form.brief,
    keywordNotes: form.keywordNotes.filter(
      (note) =>
        form.keywords.includes(note.keyword) && note.description.trim() !== '',
    ),
    topics: form.topics.filter((topic) => topic.name.trim() !== ''),
  }
}

export function labelValues(
  labels: Array<SocialTrackerLabel>,
  kind: SocialTrackerLabelKind,
) {
  return labels
    .filter((label) => label.kind === kind)
    .map((label) => label.value)
}

export function withLabelValues(
  labels: Array<SocialTrackerLabel>,
  kind: SocialTrackerLabelKind,
  values: Array<string>,
): Array<SocialTrackerLabel> {
  return [
    ...labels.filter((label) => label.kind !== kind),
    ...values.map((value) => ({ kind, value })),
  ]
}

/** The selected market owns the one market tag written to Library documents. */
export function trackerLabels(
  form: TrackerFormState,
): Array<SocialTrackerLabel> {
  return withLabelValues(
    form.labels,
    'market',
    form.market.trim() ? [form.market.trim()] : [],
  )
}

export function missingRequiredFields(
  form: TrackerFormState,
): Array<RequiredField> {
  const missing: Array<RequiredField> = []
  if (!form.name.trim()) {
    missing.push('name')
  }
  if (form.keywords.length === 0) {
    missing.push('keywords')
  }
  if (!form.language.trim()) {
    missing.push('language')
  }
  if (!form.market.trim()) {
    missing.push('market')
  }
  return missing
}

export function toTrackerInput(form: TrackerFormState): SocialTrackerInput {
  return {
    name: form.name.trim(),
    platform: form.platform,
    keywords: form.keywords,
    ...(form.handles.length > 0 ? { handles: form.handles } : {}),
    language: form.language.trim(),
    market: form.market.trim(),
    labels: trackerLabels(form),
    ...relevanceFields(form),
  }
}

export function toTrackerUpdate(form: TrackerFormState): SocialTrackerUpdate {
  return {
    name: form.name.trim(),
    keywords: form.keywords,
    handles: form.handles,
    language: form.language.trim(),
    market: form.market.trim(),
    labels: trackerLabels(form),
    ...relevanceFields(form),
  }
}
